import { useChatLauncher } from "./react.tsx";
import { useMemo, useState } from "react";
import {
  Plus,
  FolderPlus,
  Gear,
  Question,
  MagnifyingGlass,
  NotePencil,
  Warning
} from "@phosphor-icons/react";
import { withWorkerstationBase, type ChatThreadSummary } from "@workerstation/shared";

/** Recency buckets, coarsest last. */
const GROUPS = [
  { label: "Today", within: 24 * 60 * 60 * 1000 },
  { label: "Last 7 days", within: 7 * 24 * 60 * 60 * 1000 },
  { label: "Last 30 days", within: 30 * 24 * 60 * 60 * 1000 }
] as const;

function groupFor(createdAt: number, now: number): string {
  const age = now - createdAt;
  for (const group of GROUPS) if (age < group.within) return group.label;
  return "Older";
}

/** Two-letter monogram, matching the reference's square station badge. */
function monogram(stationId: string): string {
  return stationId.slice(0, 1).toUpperCase();
}

export interface StationLauncherProps {
  /** Navigate to a station, optionally opening a specific thread. */
  readonly onOpenStation?: (stationId: string, threadId?: string) => void;
}

export function StationLauncher({ onOpenStation }: StationLauncherProps) {
  const launcher = useChatLauncher(onOpenStation);
  const { stations, selected, threads, error: threadsError } = launcher.snapshot;
  const [search, setSearch] = useState("");
  const open = (stationId: string, threadId?: string) => launcher.dispatch({ _tag: "Open", stationId, ...(threadId === undefined ? {} : { threadId }) });
  const createStation = () => launcher.dispatch({ _tag: "Create" });

  const grouped = useMemo(() => {
    if (!threads) return [];
    const now = Date.now();
    const term = search.trim().toLowerCase();
    const matching = term
      ? threads.filter((thread) => thread.title.toLowerCase().includes(term))
      : threads;
    const buckets = new Map<string, ChatThreadSummary[]>();
    for (const thread of [...matching].sort((a, b) => b.createdAt - a.createdAt)) {
      const label = groupFor(thread.createdAt, now);
      const bucket = buckets.get(label);
      if (bucket) bucket.push(thread);
      else buckets.set(label, [thread]);
    }
    return [...buckets.entries()];
  }, [threads, search]);

  return (
    <div className="flex h-full w-full justify-center overflow-auto p-8">
      <div className="grid w-full max-w-4xl grid-cols-[16rem_1fr] gap-10">
        <nav aria-label="Stations" className="pt-16">
          <div className="mb-2 flex items-center justify-between px-2">
            <h2 className="text-sm font-medium text-kumo-default">Projects</h2>
            <button
              type="button"
              aria-label="New station"
              title="New station"
              onClick={createStation}
              className="rounded p-1 text-kumo-subtle hover:bg-kumo-elevated hover:text-kumo-default"
            >
              <FolderPlus size={16} />
            </button>
          </div>

          {stations.length === 0 ? (
            <p className="px-2 py-3 text-[12px] text-kumo-subtle">
              No stations opened from this browser yet. Create one to get
              started — the list is kept locally, so stations opened elsewhere
              will not appear here.
            </p>
          ) : (
            <ul className="space-y-0.5">
              {stations.map((station) => (
                <li key={station.stationId}>
                  <div
                    className={`group flex items-center gap-2 rounded-md px-2 py-1.5 ${
                      selected === station.stationId
                        ? "bg-kumo-elevated"
                        : "hover:bg-kumo-fill/60"
                    }`}
                  >
                    <button
                      type="button"
                      onClick={() => launcher.dispatch({ _tag: "Select", stationId: station.stationId })}
                      onDoubleClick={() => open(station.stationId)}
                      className="flex min-w-0 flex-1 items-center gap-2 text-left"
                    >
                      <span
                        aria-hidden="true"
                        className="flex h-5 w-5 shrink-0 items-center justify-center rounded bg-kumo-contrast/80 text-[10px] font-semibold text-kumo-base"
                      >
                        {monogram(station.stationId)}
                      </span>
                      <span className="truncate text-[13px] text-kumo-default">
                        {station.stationId}
                      </span>
                    </button>
                    <button
                      type="button"
                      aria-label={`Remove ${station.stationId} from this list`}
                      title="Remove from this list"
                      onClick={() => {
                        launcher.dispatch({ _tag: "Forget", stationId: station.stationId });
                      }}
                      className="hidden shrink-0 rounded px-1 text-[11px] text-kumo-subtle group-hover:block hover:text-kumo-default"
                    >
                      ×
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}

          <div className="mt-6 space-y-0.5 border-t border-kumo-line pt-4">
            <a
              href={withWorkerstationBase("/api/version")}
              className="flex items-center gap-2 rounded-md px-2 py-1.5 text-[13px] text-kumo-subtle hover:bg-kumo-fill/60 hover:text-kumo-default"
            >
              <Gear size={15} /> Settings
            </a>
            <a
              href="https://github.com/MedSimplesLabs/workerstation#readme"
              className="flex items-center gap-2 rounded-md px-2 py-1.5 text-[13px] text-kumo-subtle hover:bg-kumo-fill/60 hover:text-kumo-default"
            >
              <Question size={15} /> Help
            </a>
          </div>
        </nav>

        <section aria-label="Chats" className="min-w-0">
          <div className="relative mb-8">
            <MagnifyingGlass
              size={15}
              aria-hidden="true"
              className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-kumo-subtle"
            />
            <input
              type="search"
              aria-label="Search chats"
              placeholder={
                selected ? `Search chats in ${selected}` : "Search chats"
              }
              value={search}
              disabled={!selected}
              onChange={(event) => setSearch(event.target.value)}
              className="h-10 w-full rounded-lg border border-transparent bg-kumo-fill pl-9 pr-3 text-[13px] text-kumo-default outline-none placeholder:text-kumo-subtle focus:border-kumo-line"
            />
          </div>

          {!selected ? (
            <p className="text-[13px] text-kumo-subtle">
              Select a station to see its chats.
            </p>
          ) : threadsError ? (
            <p
              role="alert"
              className="flex items-center gap-2 text-[13px] text-red-500"
            >
              <Warning size={15} /> {threadsError}
            </p>
          ) : (
            <>
              <div className="mb-3 flex items-center justify-between">
                <h2 className="text-sm font-medium text-kumo-default">
                  {grouped[0]?.[0] ?? "Chats"}
                </h2>
                <button
                  type="button"
                  onClick={() => open(selected)}
                  className="flex items-center gap-1.5 rounded px-2 py-1 text-[13px] text-kumo-subtle hover:bg-kumo-fill/60 hover:text-kumo-default"
                >
                  <NotePencil size={15} /> New chat
                </button>
              </div>

              {threads === null ? (
                <p className="text-[13px] text-kumo-subtle">Loading chats…</p>
              ) : grouped.length === 0 ? (
                <p className="text-[13px] text-kumo-subtle">
                  {search.trim()
                    ? "No chats match your search."
                    : "No chats yet in this station."}
                </p>
              ) : (
                grouped.map(([label, items], index) => (
                  <div key={label} className={index > 0 ? "mt-6" : undefined}>
                    {index > 0 ? (
                      <h3 className="mb-2 text-sm font-medium text-kumo-default">
                        {label}
                      </h3>
                    ) : null}
                    <ul>
                      {items.map((thread) => (
                        <li key={thread.threadId}>
                          <button
                            type="button"
                            onClick={() => open(selected, thread.threadId)}
                            className="flex w-full items-center gap-2 rounded-md px-2 py-2 text-left hover:bg-kumo-fill/60"
                          >
                            <span
                              aria-hidden="true"
                              className="flex h-5 w-5 shrink-0 items-center justify-center rounded bg-kumo-contrast/80 text-[10px] font-semibold text-kumo-base"
                            >
                              {monogram(selected)}
                            </span>
                            <span className="truncate text-[13px] font-medium text-kumo-default">
                              {thread.title}
                            </span>
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))
              )}
            </>
          )}
        </section>
      </div>

      <button
        type="button"
        aria-label="New station"
        title="New station"
        onClick={createStation}
        className="fixed bottom-6 right-6 flex h-10 w-10 items-center justify-center rounded-full border border-kumo-line bg-kumo-base text-kumo-subtle shadow-lg hover:text-kumo-default"
      >
        <Plus size={18} />
      </button>
    </div>
  );
}
